import { subject } from "@casl/ability";
import { all, put, select, takeLatest } from "redux-saga/effects";

import { transformFormat } from "utils";

import { SUBJECT_CUSTOMER } from "../../../models/role/subject";
import { callApi } from "../../../sagas/helpers/api";
import {
  selectEnvironment,
  setError,
  setSuccess,
  startLoading,
} from "../../../store/appSlice";
import * as CustomersApi from "../api/Customers";
import { INCORPORATION_DATE_FORMAT } from "../models/Customer";
import {
  customerOperationPaths,
  fetchCustomer,
  fetchCustomerBanksByCountry,
  setCustomer,
  setCustomerBanksByCountry,
} from "./customer.slice";

import type { PayloadAction } from "@reduxjs/toolkit";
import type { CountryCode } from "../../../models/CountryCode";
import type { ObjectWithId } from "../../../utils/ObjectWithId";
import type { Customer } from "../models/Customer";
import type { CustomerCountries } from "../models/CustomerCountries";

export type UpdateCustomerBanksByCountryPayload = ObjectWithId & {
  countries: CustomerCountries;
};

const credentialMap = {
  production: "prod",
  sandbox: "sandbox",
};

function filterCredentials(customer: Customer, environment: string): Customer {
  const credentials = (customer.credentials as unknown) as CustomersApi.CustomerCredentialsFromApi[];

  return subject(SUBJECT_CUSTOMER, {
    ...customer,
    credentials: credentials
      .filter((credential) => {
        return credentialMap[credential.environment] === environment;
      })
      .map(({ createdAt, ...credential }) => {
        return {
          ...credential,
          username: credential.email,
          password: "",
        };
      })[0],
  });
}

function* fetchCustomerSaga({ payload }: PayloadAction<ObjectWithId>) {
  const { id } = payload;
  const currentEnvironment: string = yield select(selectEnvironment);
  const { details: operationPath } = customerOperationPaths(id);

  yield put(startLoading(operationPath));

  try {
    const [{ incorporationDate, ...customer }, banksByCountry]: [
      Customer,
      CountryCode[]
    ] = yield all([
      callApi(CustomersApi.getCustomer, id),
      callApi(CustomersApi.getCustomerBanksByCountry, id),
    ]);

    yield put(setCustomerBanksByCountry(banksByCountry));
    yield put(
      setCustomer({
        id,
        item: filterCredentials(
          {
            ...customer,
            incorporationDate:
              incorporationDate !== null
                ? transformFormat(incorporationDate, INCORPORATION_DATE_FORMAT)
                : incorporationDate,
          },
          currentEnvironment
        ),
      })
    );
    yield put(setSuccess(operationPath));
  } catch (error) {
    console.error(error);
    yield put(
      setError({
        path: operationPath,
        error,
      })
    );
  }
}

export default function* rootSaga() {
  yield takeLatest(
    [fetchCustomer.type, fetchCustomerBanksByCountry.type],
    fetchCustomerSaga
  );
}
